/** Client-side push notification helpers. The Settings panel calls
 *  these to subscribe / unsubscribe / list / test. The server-side
 *  fan-out lives in backend/src/push.ts; this file only handles the
 *  browser-side PushSubscription dance:
 *
 *    1. Notification.requestPermission()    user grants
 *    2. navigator.serviceWorker.ready        SW must be active
 *    3. registration.pushManager.subscribe   browser mints
 *                                             subscription with VAPID key
 *    4. POST /api/push/subscribe             server saves the row
 *
 *  The VAPID public key is fetched once per SPA boot from
 *  /api/push/vapid-public-key. The browser needs it as a Uint8Array
 *  (urlBase64ToUint8Array converts the base64url string the server
 *  hands back).
 */
import { getCsrfToken } from "./auth";

function csrfHeaders(): Record<string, string> {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

export interface PushDevice {
  id: string;
  endpoint_short: string;
  user_agent: string | null;
  created_at: string;
  last_sent_at: string | null;
  last_error: string | null;
}

export interface PushPrefs {
  enabled_kinds: string[];
}

export interface PushDevicesResponse {
  enabled: boolean;
  prefs: PushPrefs;
  devices: PushDevice[];
}

export interface PushSendResult {
  ok: boolean;
  attempted: number;
  delivered: number;
  pruned: number;
  errors: { endpoint: string; status: number | null; error: string }[];
}

export function isPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

export async function getNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) return "denied";
  return Notification.permission;
}

export async function fetchVapidPublicKey(): Promise<string> {
  const res = await fetch("/api/push/vapid-public-key", { credentials: "same-origin" });
  if (!res.ok) {
    if (res.status === 503) throw new Error("Push not configured on the server.");
    throw new Error("Failed to load VAPID public key.");
  }
  const body = (await res.json()) as { key: string };
  return body.key;
}

export async function listPushDevices(): Promise<PushDevicesResponse> {
  const res = await fetch("/api/push/devices", { credentials: "same-origin" });
  if (!res.ok) throw new Error("Failed to list devices.");
  return (await res.json()) as PushDevicesResponse;
}

/** Subscribe the current browser. Returns the saved row id. Throws
 *  with a friendly message when permission is denied or the SW is
 *  not yet registered (rare; vite-plugin-pwa registers on load). */
export async function subscribeThisDevice(): Promise<{ id: string; created: boolean }> {
  if (!isPushSupported()) throw new Error("This browser does not support push notifications.");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked. Enable them in your browser settings."
        : "Notification permission was not granted.",
    );
  }
  const reg = await navigator.serviceWorker.ready;
  const vapid = await fetchVapidPublicKey();
  const applicationServerKey = urlBase64ToUint8Array(vapid);

  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    // Re-subscribe if the existing subscription was issued for a
    // different VAPID key (e.g. server rotated keys).
    const existingKey = sub.options.applicationServerKey;
    const matches =
      existingKey &&
      sameBuffer(new Uint8Array(existingKey as ArrayBuffer), applicationServerKey);
    if (!matches) {
      try {
        await sub.unsubscribe();
      } catch {
        /* fall through */
      }
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // BufferSource cast: applicationServerKey is a Uint8Array but
      // PushManager's TS lib type insists on ArrayBuffer-backed
      // BufferSource; the runtime accepts the Uint8Array view.
      applicationServerKey: applicationServerKey as unknown as BufferSource,
    });
  }

  const json = sub.toJSON();
  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({
      subscription: {
        endpoint: json.endpoint,
        keys: json.keys,
      },
      user_agent: navigator.userAgent,
    }),
  });
  if (!res.ok) throw new Error("Failed to save subscription on the server.");
  return (await res.json()) as { id: string; created: boolean };
}

export async function unsubscribeThisDevice(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* fall through; we still want to delete the server row */
  }
  await fetch("/api/push/unsubscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ endpoint }),
  });
}

export async function sendTestPush(): Promise<PushSendResult> {
  const res = await fetch("/api/push/test", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error("Failed to send test push.");
  return (await res.json()) as PushSendResult;
}

export async function setPushPrefs(enabledKinds: string[]): Promise<PushPrefs> {
  const res = await fetch("/api/push/prefs", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    credentials: "same-origin",
    body: JSON.stringify({ enabled_kinds: enabledKinds }),
  });
  if (!res.ok) throw new Error("Failed to save preferences.");
  const body = (await res.json()) as PushPrefs;
  return body;
}

/** Whether the browser currently has an active PushSubscription
 *  (independent of whether the server still has the row). The
 *  Settings panel uses this to render the toggle in the right state
 *  even after the user clears site data on the server side. */
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameBuffer(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
